import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Badge, Button, Dialog, EmptyState, Field, Input, Select, Spinner, Textarea } from '@zt/ui';
import { Section, Table, td, tdR, th, thR, usePct } from '../components/reports/blocks';
import { istanbulToday, PeriodControls, usePeriod } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import type { HelpKey } from '@zt/help';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  PurchaseOrderList,
  Supplier,
  SupplierBalanceList,
  SupplierLedger,
  SupplierList,
  SupplierPerformance,
} from '../lib/types-faz8';
import { PoStatusBadge } from './purchase-orders';

/**
 * Tedarikçiler (F8-06, ADR-807): liste ve bakiyeler, tedarikçi kartı, cari ekstre (ödeme/iade/açılış/düzeltme,
 * ters kayıt), tedarikçi performansı. Rehber: docs/faz-8/PURCHASING_TR.md.
 */

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'] as const;

function useBalanceLabel() {
  const money = useMoney();
  return (
    b: Array<{ currency: string; balance: number; overdue: number }> | undefined,
    key: 'balance' | 'overdue',
  ) => (b && b.length ? b.map((x) => money(x[key], x.currency)).join(' · ') : '—');
}

export function SuppliersPage() {
  const t = useT();
  const { can } = usePermissions();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const list = useQuery({
    queryKey: ['suppliers', q],
    queryFn: () =>
      api.get<SupplierList>(`/v1/admin/suppliers${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const balances = useQuery({
    queryKey: ['supplier-balances'],
    queryFn: () => api.get<SupplierBalanceList>('/v1/admin/suppliers/balances'),
  });
  const balanceOf = (id: string) => balances.data?.find((b) => b.supplierId === id)?.balances;
  const label = useBalanceLabel();
  return (
    <PageFrame
      helpKey="page.suppliers"
      actions={
        can('purchasing.manage') && (
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.purchasing.addSupplier')}
          </Button>
        )
      }
    >
      <Field label={t('common.search')} className="max-w-sm">
        {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
      </Field>
      {list.isError ? (
        <p role="alert" className="text-sm text-rose">
          {list.error instanceof ApiError ? list.error.title : String(list.error)}
        </p>
      ) : !list.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data.length ? (
        <EmptyState
          title={t('admin.purchasing.noSuppliers')}
          description={t('admin.purchasing.noSuppliersLead')}
        />
      ) : (
        <div className="mt-4">
          <Table
            caption={t('admin.purchasing.navSuppliers')}
            head={
              <tr>
                <th scope="col" className={th}>
                  {t('admin.purchasing.supplierCols.supplier')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.purchasing.supplierCols.contact')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.purchasing.supplierCols.terms')}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.purchasing.supplierCols.balance')}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.purchasing.supplierCols.overdue')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.purchasing.supplierCols.status')}
                </th>
              </tr>
            }
          >
            {list.data.map((s) => (
              <tr key={s.id} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-normal`}>
                  <Link
                    to="/tedarikciler/$id"
                    params={{ id: s.id }}
                    className="font-medium text-pine hover:underline"
                  >
                    {s.name}
                  </Link>
                  <span className="block text-xs text-ink-3">{s.code}</span>
                </th>
                <td className={td}>
                  {s.contactName ?? '—'}
                  {(s.phone || s.email) && (
                    <span className="block text-xs text-ink-3">
                      {[s.phone, s.email].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </td>
                <td className={td}>
                  {s.paymentTermDays
                    ? t('admin.purchasing.termsDays', { days: s.paymentTermDays })
                    : t('admin.purchasing.cash')}
                </td>
                <td className={tdR}>{label(balanceOf(s.id), 'balance')}</td>
                <td className={tdR}>{label(balanceOf(s.id), 'overdue')}</td>
                <td className={td}>
                  <Badge tone={s.status === 'active' ? 'moss' : 'slate'}>
                    {t(`admin.purchasing.supplierStatus.${s.status}`)}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}
      <Performance />
      {editing && (
        <SupplierDialog supplier={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </PageFrame>
  );
}

function Performance() {
  const t = useT();
  const money = useMoney();
  const pct = usePct();
  const { formatNumber } = useI18n();
  const period = usePeriod('last_90');
  const q = useQuery({
    queryKey: ['supplier-performance', period.from, period.to],
    queryFn: () =>
      api.get<SupplierPerformance>(`/v1/admin/reports/suppliers?from=${period.from}&to=${period.to}`),
    placeholderData: keepPreviousData,
  });
  const rows = (q.data?.rows ?? []).filter((r) => r.orders > 0 || r.spend > 0);
  return (
    <div className="mt-10">
      <Section title={t('admin.purchasing.performanceTitle')} help={<HelpTip k="purchasing.performance" />}>
        <p className="mb-3 text-sm text-ink-2">{t('admin.purchasing.performanceLead')}</p>
        <div className="mb-3 flex flex-wrap items-end gap-4">
          <PeriodControls period={period} showInterval={false} />
        </div>
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : !rows.length ? (
          <EmptyState title={t('admin.reports.empty')} />
        ) : (
          <div className={q.isPlaceholderData ? 'opacity-60' : undefined}>
            <Table
              caption={t('admin.purchasing.performanceTitle')}
              head={
                <tr>
                  {(['supplier', 'orders', 'onTime', 'fill', 'short', 'over', 'lead', 'spend'] as const).map(
                    (k) => (
                      <th key={k} scope="col" className={k === 'supplier' ? th : thR}>
                        {t(`admin.purchasing.perfCols.${k}`)}
                      </th>
                    ),
                  )}
                </tr>
              }
            >
              {rows.map((r) => (
                <tr key={r.supplierId} className="border-t border-line">
                  <th scope="row" className={`${td} text-start font-normal`}>
                    {r.name}
                  </th>
                  <td className={tdR}>{formatNumber(r.orders, 0)}</td>
                  <td className={tdR}>
                    {pct(r.onTimeBps)}
                    {r.late > 0 && (
                      <span className="block text-xs text-ink-3">
                        {t('admin.purchasing.late', { count: r.late })}
                      </span>
                    )}
                  </td>
                  <td className={tdR}>{pct(r.fillRateBps)}</td>
                  <td className={tdR}>{formatNumber(r.shortLines, 0)}</td>
                  <td className={tdR}>{formatNumber(r.overLines, 0)}</td>
                  <td className={tdR}>
                    {r.averageLeadDays === null
                      ? '—'
                      : t('admin.purchasing.termsDays', { days: formatNumber(r.averageLeadDays, 0) })}
                  </td>
                  <td className={tdR}>{money(r.spend, 'TRY')}</td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ------------------------------------------------------------------------------------------ Tedarikçi formu

function SupplierDialog({ supplier, onClose }: { supplier: Supplier | null; onClose: () => void }) {
  const t = useT();
  const [idem] = useState(() => new IdempotencyKey());
  const [f, setF] = useState({
    name: supplier?.name ?? '',
    code: supplier?.code ?? '',
    taxOffice: supplier?.taxOffice ?? '',
    taxNumber: supplier && !supplier.piiMasked ? (supplier.taxNumber ?? '') : '',
    contactName: supplier?.contactName ?? '',
    email: supplier?.email ?? '',
    phone: supplier?.phone ?? '',
    address: supplier?.address ?? '',
    currency: supplier?.currency ?? 'TRY',
    paymentTermDays: String(supplier?.paymentTermDays ?? 0),
    leadTimeDays:
      supplier?.leadTimeDays === null || supplier?.leadTimeDays === undefined
        ? ''
        : String(supplier.leadTimeDays),
    note: supplier?.note ?? '',
    status: supplier?.status ?? 'active',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const nul = (v: string) => v.trim() || null;
  const save = useApiMutation(
    () => {
      const body: Record<string, unknown> = {
        name: f.name.trim(),
        code: f.code.trim(),
        taxOffice: nul(f.taxOffice),
        contactName: nul(f.contactName),
        email: nul(f.email),
        phone: nul(f.phone),
        address: nul(f.address),
        currency: f.currency,
        paymentTermDays: Number(f.paymentTermDays || 0),
        leadTimeDays: f.leadTimeDays.trim() ? Number(f.leadTimeDays) : null,
        note: nul(f.note),
        status: f.status,
      };
      // Maskeli vergi numarası geri gönderilmez (değiştirilmediyse alan hiç gönderilmez).
      if (!supplier || !supplier.piiMasked || f.taxNumber) body.taxNumber = nul(f.taxNumber);
      return supplier
        ? api.patch(`/v1/admin/suppliers/${supplier.id}`, body, { ifMatch: supplier.version })
        : api.post('/v1/admin/suppliers', body, { idempotencyKey: idem });
    },
    {
      invalidate: [['suppliers'], ['supplier', supplier?.id ?? '']],
      success: t('admin.purchasing.supplierSaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const fe = (k: string) => fieldError(save.error, k);
  const text = (k: keyof typeof f, opts: { required?: boolean; type?: string; help?: HelpKey } = {}) => (
    <Field
      label={t(`admin.purchasing.fields.${k}`)}
      required={opts.required}
      optionalLabel={opts.required ? undefined : t('common.optional')}
      error={fe(k)}
      help={opts.help ? <HelpTip k={opts.help} /> : undefined}
    >
      {(p) => (
        <Input {...p} type={opts.type ?? 'text'} value={f[k]} onChange={(e) => set(k)(e.target.value)} />
      )}
    </Field>
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={supplier ? t('admin.purchasing.editSupplier') : t('admin.purchasing.addSupplier')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={f.name.trim().length < 2 || !f.code.trim()}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.purchasing.saveSupplier')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {text('name', { required: true })}
        {text('code', { required: true, help: 'purchasing.code' })}
        {text('taxOffice')}
        {text('taxNumber')}
        {text('contactName')}
        {text('email', { type: 'email' })}
        {text('phone', { type: 'tel' })}
        <Field label={t('admin.purchasing.fields.currency')} required>
          {(p) => (
            <Select {...p} value={f.currency} onChange={(e) => set('currency')(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.purchasing.fields.paymentTermDays')}
          help={<HelpTip k="purchasing.payment_terms" />}
          error={fe('paymentTermDays')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={365}
              value={f.paymentTermDays}
              onChange={(e) => set('paymentTermDays')(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('admin.purchasing.fields.leadTimeDays')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="purchasing.lead_time" />}
          error={fe('leadTimeDays')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={365}
              value={f.leadTimeDays}
              onChange={(e) => set('leadTimeDays')(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('admin.purchasing.fields.status')}>
          {(p) => (
            <Select {...p} value={f.status} onChange={(e) => set('status')(e.target.value)}>
              <option value="active">{t('admin.purchasing.supplierStatus.active')}</option>
              <option value="inactive">{t('admin.purchasing.supplierStatus.inactive')}</option>
            </Select>
          )}
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('admin.purchasing.fields.address')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Textarea {...p} rows={2} value={f.address} onChange={(e) => set('address')(e.target.value)} />
            )}
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={t('admin.purchasing.fields.note')} optionalLabel={t('common.optional')}>
            {(p) => <Textarea {...p} rows={2} value={f.note} onChange={(e) => set('note')(e.target.value)} />}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------------ Tedarikçi ayrıntısı

export function SupplierDetailPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/tedarikciler/$id' });
  const { can } = usePermissions();
  const { formatDate } = useI18n();
  const money = useMoney();
  const [editing, setEditing] = useState(false);
  const s = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => api.get<Supplier>(`/v1/admin/suppliers/${id}`),
  });
  const orders = useQuery({
    queryKey: ['purchase-orders', 'supplier', id],
    queryFn: () => api.get<PurchaseOrderList>(`/v1/admin/purchase-orders?supplierId=${id}`),
  });
  if (s.isError)
    return (
      <PageFrame helpKey="page.supplier">
        <p role="alert" className="text-sm text-rose">
          {s.error instanceof ApiError ? s.error.title : String(s.error)}
        </p>
      </PageFrame>
    );
  if (!s.data)
    return (
      <PageFrame helpKey="page.supplier">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const sup = s.data;
  const info: Array<[string, string | null]> = [
    [t('admin.purchasing.fields.code'), sup.code],
    [t('admin.purchasing.fields.taxOffice'), sup.taxOffice],
    [t('admin.purchasing.fields.taxNumber'), sup.taxNumber],
    [t('admin.purchasing.fields.contactName'), sup.contactName],
    [t('admin.purchasing.fields.email'), sup.email],
    [t('admin.purchasing.fields.phone'), sup.phone],
    [t('admin.purchasing.fields.currency'), sup.currency],
    [
      t('admin.purchasing.fields.paymentTermDays'),
      sup.paymentTermDays
        ? t('admin.purchasing.termsDays', { days: sup.paymentTermDays })
        : t('admin.purchasing.cash'),
    ],
    [
      t('admin.purchasing.fields.leadTimeDays'),
      sup.leadTimeDays === null ? null : t('admin.purchasing.termsDays', { days: sup.leadTimeDays }),
    ],
    [t('admin.purchasing.fields.address'), sup.address],
    [t('admin.purchasing.fields.note'), sup.note],
  ];
  return (
    <PageFrame
      helpKey="page.supplier"
      title={sup.name}
      actions={
        can('purchasing.manage') && (
          <Button onClick={() => setEditing(true)}>{t('admin.purchasing.editSupplier')}</Button>
        )
      }
    >
      <Badge tone={sup.status === 'active' ? 'moss' : 'slate'}>
        {t(`admin.purchasing.supplierStatus.${sup.status}`)}
      </Badge>
      <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {info
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <dt className="min-w-36 text-ink-3">{k}</dt>
              <dd className="text-ink">{v}</dd>
            </div>
          ))}
      </dl>
      <Ledger supplierId={id} currency={sup.currency} />
      <div className="mt-10">
        <Section title={t('admin.purchasing.ordersOfSupplier')}>
          {!orders.data ? (
            <Spinner label={t('common.loading')} />
          ) : !orders.data.length ? (
            <EmptyState title={t('admin.purchasing.noOrders')} />
          ) : (
            <Table
              caption={t('admin.purchasing.ordersOfSupplier')}
              head={
                <tr>
                  <th scope="col" className={th}>
                    {t('admin.purchasing.orderCols.number')}
                  </th>
                  <th scope="col" className={th}>
                    {t('admin.purchasing.orderCols.expected')}
                  </th>
                  <th scope="col" className={th}>
                    {t('admin.purchasing.orderCols.status')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.purchasing.orderCols.total')}
                  </th>
                </tr>
              }
            >
              {orders.data.map((o) => (
                <tr key={o.id} className="border-t border-line">
                  <th scope="row" className={`${td} text-start font-normal`}>
                    <Link
                      to="/satin-alma/$id"
                      params={{ id: o.id }}
                      className="font-medium text-pine hover:underline"
                    >
                      {o.number}
                    </Link>
                  </th>
                  <td className={td}>{o.expectedDate ? formatDate(o.expectedDate, false) : '—'}</td>
                  <td className={td}>
                    <PoStatusBadge status={o.status} />
                  </td>
                  <td className={tdR}>{money(o.totalMinor, o.currency)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Section>
      </div>
      {editing && <SupplierDialog supplier={sup} onClose={() => setEditing(false)} />}
    </PageFrame>
  );
}

function Ledger({ supplierId, currency }: { supplierId: string; currency: string }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['supplier-ledger', supplierId],
    queryFn: () => api.get<SupplierLedger>(`/v1/admin/suppliers/${supplierId}/ledger`),
  });
  const manage = can('purchasing.ledger.manage');
  return (
    <div className="mt-10">
      <Section title={t('admin.purchasing.ledgerTitle')} help={<HelpTip k="purchasing.ledger_entry" />}>
        <p className="mb-2 max-w-[80ch] text-sm text-ink-2">{t('admin.purchasing.ledgerLead')}</p>
        {manage && (
          <Button className="mb-3" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.purchasing.addEntry')}
          </Button>
        )}
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <>
            {q.data.balances.map((b) => (
              <p key={b.currency} role="status" className="mb-2 text-sm font-medium text-ink">
                {t('admin.purchasing.balanceLine', {
                  amount: money(b.balance, b.currency),
                  overdue: money(b.overdue, b.currency),
                })}
              </p>
            ))}
            {!q.data.entries.length ? (
              <EmptyState title={t('admin.purchasing.noEntries')} />
            ) : (
              <Table
                caption={t('admin.purchasing.ledgerTitle')}
                head={
                  <tr>
                    {(['date', 'kind', 'description', 'due', 'amount', 'balance'] as const).map((k) => (
                      <th key={k} scope="col" className={k === 'amount' || k === 'balance' ? thR : th}>
                        {t(`admin.purchasing.ledgerCols.${k}`)}
                      </th>
                    ))}
                    {manage && (
                      <th scope="col" className={th}>
                        <span className="sr-only">{t('admin.purchasing.reverse')}</span>
                      </th>
                    )}
                  </tr>
                }
              >
                {q.data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-line">
                    <td className={`${td} whitespace-nowrap`}>{formatDate(e.entryDate, false)}</td>
                    <th scope="row" className={`${td} text-start font-normal`}>
                      {t(`admin.purchasing.kinds.${e.kind}`)}
                      {e.method && (
                        <span className="block text-xs text-ink-3">
                          {t(`admin.purchasing.methods.${e.method}`)}
                        </span>
                      )}
                      {e.reversed && (
                        <span className="block text-xs text-ink-3">{t('admin.purchasing.reversedMark')}</span>
                      )}
                    </th>
                    <td className={td}>{[e.description, e.reference].filter(Boolean).join(' · ') || '—'}</td>
                    <td className={`${td} whitespace-nowrap`}>
                      {e.dueDate ? formatDate(e.dueDate, false) : '—'}
                    </td>
                    <td className={tdR}>{money(e.amountMinor, e.currency)}</td>
                    <td className={tdR}>{money(e.balanceMinor, e.currency)}</td>
                    {manage && (
                      <td className={td}>
                        {e.reversible && (
                          <Button size="sm" variant="ghost" onClick={() => setReversing(e.id)}>
                            {t('admin.purchasing.reverse')}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </Table>
            )}
          </>
        )}
      </Section>
      {adding && <EntryDialog supplierId={supplierId} currency={currency} onClose={() => setAdding(false)} />}
      {reversing && (
        <ReverseDialog entryId={reversing} supplierId={supplierId} onClose={() => setReversing(null)} />
      )}
    </div>
  );
}

function EntryDialog({
  supplierId,
  currency,
  onClose,
}: {
  supplierId: string;
  currency: string;
  onClose: () => void;
}) {
  const t = useT();
  const [idem] = useState(() => new IdempotencyKey());
  const [f, setF] = useState({
    kind: 'payment',
    amount: '',
    direction: 'credit',
    entryDate: istanbulToday(),
    dueDate: '',
    method: 'bank_transfer',
    reference: '',
    description: '',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const amount = toMinor(f.amount, currency);
  const save = useApiMutation(
    () =>
      api.post(
        `/v1/admin/suppliers/${supplierId}/ledger`,
        {
          kind: f.kind,
          amountMinor: amount!,
          currency,
          direction: f.kind === 'adjustment' ? f.direction : undefined,
          entryDate: f.entryDate,
          dueDate:
            (f.kind === 'opening' || (f.kind === 'adjustment' && f.direction === 'debit')) && f.dueDate
              ? f.dueDate
              : undefined,
          method: f.kind === 'payment' ? f.method : undefined,
          reference: f.reference.trim() || null,
          description: f.description.trim() || null,
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['supplier-ledger', supplierId], ['supplier-balances']],
      success: t('admin.purchasing.entrySaved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const fe = (k: string) => fieldError(save.error, k);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.purchasing.addEntry')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!amount || amount <= 0}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.purchasing.saveEntry')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('admin.purchasing.entryFields.kind')}
          required
          help={<HelpTip k="purchasing.ledger_entry" />}
        >
          {(p) => (
            <Select {...p} value={f.kind} onChange={(e) => set('kind')(e.target.value)}>
              {(['payment', 'return', 'opening', 'adjustment'] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`admin.purchasing.kinds.${k}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={`${t('admin.purchasing.entryFields.amount')} (${currency})`}
          required
          error={f.amount && amount === null ? t('admin.purchasing.costInvalid') : fe('amountMinor')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={f.amount}
              onChange={(e) => set('amount')(e.target.value)}
            />
          )}
        </Field>
        {f.kind === 'adjustment' && (
          <Field label={t('admin.purchasing.entryFields.direction')} required error={fe('direction')}>
            {(p) => (
              <Select {...p} value={f.direction} onChange={(e) => set('direction')(e.target.value)}>
                <option value="credit">{t('admin.purchasing.directions.credit')}</option>
                <option value="debit">{t('admin.purchasing.directions.debit')}</option>
              </Select>
            )}
          </Field>
        )}
        {f.kind === 'payment' && (
          <Field label={t('admin.purchasing.entryFields.method')} required error={fe('method')}>
            {(p) => (
              <Select {...p} value={f.method} onChange={(e) => set('method')(e.target.value)}>
                {(['bank_transfer', 'cash', 'cheque', 'note', 'card', 'other'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`admin.purchasing.methods.${m}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.purchasing.entryFields.date')} required error={fe('entryDate')}>
          {(p) => (
            <Input
              {...p}
              type="date"
              max={istanbulToday()}
              value={f.entryDate}
              onChange={(e) => set('entryDate')(e.target.value)}
            />
          )}
        </Field>
        {(f.kind === 'opening' || (f.kind === 'adjustment' && f.direction === 'debit')) && (
          <Field label={t('admin.purchasing.entryFields.dueDate')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input {...p} type="date" value={f.dueDate} onChange={(e) => set('dueDate')(e.target.value)} />
            )}
          </Field>
        )}
        <Field label={t('admin.purchasing.entryFields.reference')} optionalLabel={t('common.optional')}>
          {(p) => <Input {...p} value={f.reference} onChange={(e) => set('reference')(e.target.value)} />}
        </Field>
        <div className="sm:col-span-2">
          <Field
            label={t('admin.purchasing.entryFields.description')}
            required={f.kind === 'adjustment'}
            optionalLabel={f.kind === 'adjustment' ? undefined : t('common.optional')}
            error={fe('description')}
          >
            {(p) => (
              <Input {...p} value={f.description} onChange={(e) => set('description')(e.target.value)} />
            )}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

function ReverseDialog({
  entryId,
  supplierId,
  onClose,
}: {
  entryId: string;
  supplierId: string;
  onClose: () => void;
}) {
  const t = useT();
  const [idem] = useState(() => new IdempotencyKey());
  const [reason, setReason] = useState('');
  const save = useApiMutation(
    () =>
      api.post(
        `/v1/admin/supplier-ledger/${entryId}/reverse`,
        { description: reason.trim() },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['supplier-ledger', supplierId], ['supplier-balances']],
      success: t('admin.purchasing.reversed'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.purchasing.reverseTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={save.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.purchasing.reverseSave')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.purchasing.reverseReason')}
        required
        error={fieldError(save.error, 'description')}
      >
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
